import { VaroAdvanceOption } from '@varo/graphql-schema-types';
import { useFocusEffect, useRouter } from 'expo-router';
import {
  ANIMATION_DURATION,
  INITIAL_HEIGHT,
} from 'features/advance/constants/constants';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Button from 'shared/components/vxl/Button/Button';
import LendingCard from 'shared/components/vxl/cards/LendingCard/LendingCard';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

const GAP = theme.spacing.s;
interface AmountsProps {
  options: VaroAdvanceOption[];
}

const Amounts = ({ options }: AmountsProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'amountSelection',
  });

  const showMoreText = translatedText('showMoreButton');
  const showLessText = translatedText('showLessButton');

  const [amountOptions, setAmountOptions] = useState<VaroAdvanceOption[]>([]);
  const [showButton, setShowButton] = useState(false);
  const [isShowMoreText, setIsShowMoreText] = useState(true);
  const [amountHeight, setAmountHeight] = useState<number>(INITIAL_HEIGHT);
  const animation = useSharedValue(amountHeight ?? 0);

  useFocusEffect(
    useCallback(() => {
      if (options?.length > 2) {
        const reducedOptions = options.slice(0, 2);
        setAmountOptions(reducedOptions);
        setShowButton(true);
      } else {
        setAmountOptions(options);
      }

      return () => {
        setIsShowMoreText(true);
      };
    }, [options]),
  );

  const handleShowMorePressAnalytics = usePressableAnalytics({
    analyticsId: 'showMoreClicked',
  });

  const handleOnButtonPress = () => {
    handleShowMorePressAnalytics();
    const expandedStateValue = isShowMoreText
      ? Math.ceil(options.length / 2) * (amountHeight + GAP)
      : amountHeight;

    animation.value = withTiming(expandedStateValue, {
      duration: ANIMATION_DURATION,
    });

    if (isShowMoreText) {
      setAmountOptions(options);
    } else {
      const reducedOptions = options?.slice(0, 2);
      setAmountOptions(reducedOptions);
    }

    setIsShowMoreText(!isShowMoreText);
  };

  const animatedStyle = useAnimatedStyle(() => {
    return {
      height: !isShowMoreText ? animation.value : INITIAL_HEIGHT,
    };
  });

  if (amountOptions.length === 0) {
    return null;
  }

  return (
    <>
      <Animated.View
        style={[styles.amountsContainer, animatedStyle]}
        testID="amountsContainer"
      >
        {amountOptions?.map((option, i) => (
          <View
            style={[styles.amount, i % 2 ? styles.even : styles.odd]}
            key={option?.id}
            onLayout={(event) => {
              const { height } = event.nativeEvent.layout;
              setAmountHeight(height);
            }}
          >
            <LendingCard
              isDisabled={false}
              text={option?.title}
              details={[
                {
                  id: Number(option?.amount.amount),
                  titleText: translatedText('amountSelectionButtonTitle'),
                  subtitleText: option?.fee?.formatted,
                },
              ]}
              onPress={() => {
                router.push({
                  pathname: '/varo-advance/autopayment',
                  params: {
                    selectedAmount: option.amount.amount,
                    feeAmount: option.fee.amount,
                    optionId: option.id,
                    qualificationMethod: option.qualificationMethod,
                  },
                });
              }}
              isSmallCard={false}
              analyticsId="vaAmountSelected"
              customAttributes={{
                amount: option ? String(option.amount.amount) : '',
              }}
            />
          </View>
        ))}
      </Animated.View>
      {showButton ? (
        <View style={styles.buttonContainer}>
          <Button
            type="TEXT"
            onPress={handleOnButtonPress}
            label={isShowMoreText ? showMoreText : showLessText}
          />
        </View>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  amount: {
    flexBasis: '50%',
    flexGrow: 0,
    flexShrink: 1,
  },
  amountsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: GAP,
  },
  buttonContainer: {
    marginVertical: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  even: { paddingLeft: theme.spacing.xs },
  odd: { paddingRight: theme.spacing.xs },
});

export default Amounts;
